import type { Circuit, CircuitGenerationParams } from '../types/track.js';

export const CIRCUIT_BACKUP_FORMAT = 'multirx-circuits' as const;
export const CIRCUIT_BACKUP_VERSION = 1;

export interface CircuitBackupEntry {
  name?: string | null;
  created_at?: string;
  params: CircuitGenerationParams;
  circuit: Circuit;
  /** Identifiant d’origine (informatif, non réutilisé à l’import). */
  sourceId?: number;
}

export interface CircuitBackupFile {
  format: typeof CIRCUIT_BACKUP_FORMAT;
  version: typeof CIRCUIT_BACKUP_VERSION;
  exportedAt: string;
  circuits: CircuitBackupEntry[];
}

export interface ParsedCircuitBackup {
  circuits: CircuitBackupEntry[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isCircuitGenerationParams(value: unknown): value is CircuitGenerationParams {
  if (!isRecord(value)) return false;
  return (
    typeof value.numStraights === 'number' &&
    typeof value.numTurns === 'number' &&
    typeof value.approximateCells === 'number'
  );
}

function isCircuit(value: unknown): value is Circuit {
  if (!isRecord(value)) return false;
  return (
    Array.isArray(value.path) &&
    value.path.length > 0 &&
    typeof value.width === 'number' &&
    typeof value.height === 'number' &&
    Array.isArray(value.grid)
  );
}

function parseCircuitBackupEntry(raw: unknown, index: number): CircuitBackupEntry {
  if (!isRecord(raw)) {
    throw new Error(`Entrée ${index + 1} : format invalide`);
  }
  if (!isCircuit(raw.circuit)) {
    throw new Error(`Entrée ${index + 1} : circuit invalide ou sans tracé`);
  }
  if (!isCircuitGenerationParams(raw.params)) {
    throw new Error(`Entrée ${index + 1} : paramètres de génération manquants`);
  }
  const entry: CircuitBackupEntry = {
    name: typeof raw.name === 'string' ? raw.name : raw.name === null ? null : undefined,
    created_at: typeof raw.created_at === 'string' ? raw.created_at : undefined,
    params: raw.params,
    circuit: raw.circuit,
  };
  if (typeof raw.sourceId === 'number') {
    entry.sourceId = raw.sourceId;
  } else if (typeof raw.id === 'number') {
    entry.sourceId = raw.id;
  }
  return entry;
}

export function buildCircuitBackupFile(
  entries: CircuitBackupEntry[],
  exportedAt: string = new Date().toISOString()
): CircuitBackupFile {
  return {
    format: CIRCUIT_BACKUP_FORMAT,
    version: CIRCUIT_BACKUP_VERSION,
    exportedAt,
    circuits: entries,
  };
}

export function circuitBackupEntryFromSaved(saved: {
  id?: number;
  name?: string | null;
  created_at?: string;
  params: CircuitGenerationParams;
  circuit: Circuit;
}): CircuitBackupEntry {
  return {
    name: saved.name,
    created_at: saved.created_at,
    params: saved.params,
    circuit: saved.circuit,
    ...(saved.id != null ? { sourceId: saved.id } : {}),
  };
}

export function circuitBackupFilename(exportedAt: Date = new Date()): string {
  const stamp = exportedAt.toISOString().slice(0, 19).replace(/[:T]/g, '-');
  return `multirx-circuits-${stamp}.json`;
}

/** Accepte un fichier de sauvegarde MultiRX ou un circuit exporté seul. */
export function parseCircuitBackupPayload(raw: unknown): ParsedCircuitBackup {
  if (isRecord(raw) && raw.circuit && raw.params && !raw.circuits) {
    return { circuits: [parseCircuitBackupEntry(raw, 0)] };
  }

  if (!isRecord(raw) || raw.format !== CIRCUIT_BACKUP_FORMAT) {
    throw new Error('Fichier non reconnu (format multirx-circuits attendu)');
  }
  if (raw.version !== CIRCUIT_BACKUP_VERSION) {
    throw new Error(`Version de sauvegarde non supportée : ${String(raw.version)}`);
  }
  if (!Array.isArray(raw.circuits) || raw.circuits.length === 0) {
    throw new Error('Aucun circuit dans le fichier');
  }

  return { circuits: raw.circuits.map((entry, index) => parseCircuitBackupEntry(entry, index)) };
}
